import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  Divider,
  UnorderedList,
  ListItem,
  OrderedList,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Footer from './Footer';

const OptInPolicy = () => (
  <Container maxW="container.md" py={8}>
    <VStack spacing={6} align="stretch">
      <Heading as="h1" size="xl">SMS Opt-In & Consent Policy</Heading>
      <Text fontSize="sm" color="gray.600">Last Updated: April 3, 2026</Text>
      <Text fontSize="sm" color="gray.600">
        Operated by <strong>Setora, LLC</strong> on behalf of PlayMaker Pics clients
      </Text>
      <Divider />

      <Box>
        <Heading as="h2" size="lg" mb={4}>Program Overview</Heading>
        <Text mb={4}>
          PlayMaker Pics, a service operated by <strong>Setora, LLC</strong>, partners with youth sports
          organizations to create personalized photography and graphics for athletes. As part of this
          service, we send SMS text messages to parents, guardians, and authorized contacts to notify
          them about their athlete's photos, order updates, and important account activity.
        </Text>
        <Text>
          This policy describes how end users opt in to receive SMS messages from PlayMaker Pics,
          thereby giving consent to the sender to receive text messages. If multiple opt-in methods
          are available, all methods are described below.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>How You Opt In (Consent Methods)</Heading>
        <Text mb={3}>
          You may opt in to receive SMS messages from PlayMaker Pics through any of the following methods:
        </Text>

        <Heading as="h3" size="md" mb={2} mt={4}>1. Web Registration Form</Heading>
        <Text mb={3}>
          When you create an account on PlayMaker Pics or sign up for a campaign/event through our
          website, you may provide your mobile phone number and check a box explicitly consenting
          to receive SMS text messages. The consent checkbox reads:
        </Text>
        <Alert status="info" borderRadius="md" mb={3}>
          <AlertIcon />
          <Text fontSize="sm">
            "I agree to receive SMS text messages from PlayMaker Pics regarding my athlete's photos,
            order updates, and account notifications. Message and data rates may apply. Message
            frequency varies. Reply <strong>STOP</strong> to opt out. Reply <strong>HELP</strong> for
            help. View our{' '}
            <Link as={RouterLink} to="/terms" color="blue.500">Terms of Service</Link> and{' '}
            <Link as={RouterLink} to="/privacy" color="blue.500">Privacy Policy</Link>."
          </Text>
        </Alert>

        <Heading as="h3" size="md" mb={2} mt={4}>2. Event/Campaign Sign-Up (In-Person or Online)</Heading>
        <Text mb={3}>
          When a parent or guardian registers their athlete for an event or campaign through a
          PlayMaker Pics sign-up form (either online or via a link provided by the sports organization),
          they may provide their phone number and explicitly opt in to receive SMS notifications by
          checking a consent checkbox on the sign-up form.
        </Text>

        <Heading as="h3" size="md" mb={2} mt={4}>3. Organization-Provided Roster (With Prior Consent)</Heading>
        <Text mb={3}>
          A youth sports organization may provide PlayMaker Pics with a roster of participating athletes
          and their parent/guardian contact details, including phone numbers. In this case:
        </Text>
        <OrderedList spacing={2} pl={5} mb={3}>
          <ListItem>
            The organization confirms that each parent/guardian has authorized the organization to
            share their contact information with PlayMaker Pics for the purpose of receiving SMS
            notifications related to their athlete's photos and services.
          </ListItem>
          <ListItem>
            PlayMaker Pics imports the roster and creates unverified user profiles. These contacts
            are <strong>not</strong> sent SMS messages until they individually confirm consent.
          </ListItem>
          <ListItem>
            Each parent/guardian receives an initial outreach (via email or a single introductory text)
            with a link to confirm their phone number and explicitly opt in to ongoing SMS notifications
            by completing the consent process on our website.
          </ListItem>
        </OrderedList>

        <Heading as="h3" size="md" mb={2} mt={4}>4. Account Dashboard Opt-In</Heading>
        <Text mb={3}>
          Existing users who did not previously opt in to SMS can enable SMS notifications at any time
          from their account settings/dashboard by entering their phone number and checking the consent
          checkbox.
        </Text>

        <Heading as="h3" size="md" mb={2} mt={4}>5. Keyword Opt-In (Text-to-Join)</Heading>
        <Text mb={3}>
          Users may text a specific keyword (e.g., "JOIN" or a campaign-specific keyword) to our
          designated phone number to opt in. After texting the keyword, the user will receive a
          confirmation message with program details and must reply "YES" to confirm their opt-in.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>What Messages You Will Receive</Heading>
        <Text mb={3}>Once you opt in, you may receive SMS messages related to:</Text>
        <UnorderedList spacing={2} pl={5} mb={4}>
          <ListItem>Notifications that new photos of your athlete are available for viewing</ListItem>
          <ListItem>Order confirmations, shipping updates, and delivery status</ListItem>
          <ListItem>Account verification and security alerts</ListItem>
          <ListItem>Event/campaign reminders and deadlines</ListItem>
          <ListItem>Special offers or promotions related to your athlete's photos</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>Message Frequency</Heading>
        <Text mb={3}>
          Message frequency varies based on your activity and the campaigns you are enrolled in.
          Typically, you can expect to receive between 1 and 10 messages per month. During active
          photo sessions or events, message frequency may temporarily increase.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>Message and Data Rates</Heading>
        <Text mb={3}>
          <strong>Message and data rates may apply.</strong> Standard messaging rates from your
          wireless carrier apply to all SMS messages sent and received. PlayMaker Pics does not
          charge a fee for SMS notifications, but your carrier may charge you for each text message
          you send or receive. Contact your wireless carrier for details about your messaging plan.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>How to Opt Out</Heading>
        <Text mb={3}>
          You can opt out of receiving SMS messages at any time using any of the following methods:
        </Text>
        <UnorderedList spacing={2} pl={5} mb={4}>
          <ListItem>
            <Text>
              Reply <strong>STOP</strong> to any SMS message you receive from PlayMaker Pics.
              You will receive a one-time confirmation message that you have been unsubscribed.
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              Disable SMS notifications from your account settings/dashboard on our website.
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              Contact us at{' '}
              <Link href="mailto:support@playmakerpics.com" color="blue.500">support@playmakerpics.com</Link>{' '}
              and request to be removed from SMS communications.
            </Text>
          </ListItem>
        </UnorderedList>
        <Text>
          After opting out, you will no longer receive SMS messages from PlayMaker Pics unless you
          opt in again. Opting out of SMS does not affect your ability to use our website or services.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>How to Get Help</Heading>
        <Text mb={3}>
          If you need assistance or have questions about our SMS program:
        </Text>
        <UnorderedList spacing={2} pl={5} mb={4}>
          <ListItem>
            <Text>
              Reply <strong>HELP</strong> to any SMS message you receive from PlayMaker Pics
              for support information.
            </Text>
          </ListItem>
          <ListItem>
            <Text>
              Email us at{' '}
              <Link href="mailto:support@playmakerpics.com" color="blue.500">support@playmakerpics.com</Link>
            </Text>
          </ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>Consent is Not a Condition of Purchase</Heading>
        <Text>
          Opting in to SMS messages is not required to purchase products or use our services.
          You can still browse photos, place orders, and access your account without subscribing
          to SMS notifications.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>No Information Sharing</Heading>
        <Text>
          We do not sell, rent, or share your phone number or SMS opt-in consent with third parties
          for their marketing purposes. Your information is used solely for the purposes described
          in this policy and our{' '}
          <Link as={RouterLink} to="/privacy" color="blue.500">Privacy Policy</Link>.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>Legal Compliance</Heading>
        <UnorderedList spacing={3} pl={5}>
          <ListItem>
            SMS consent is collected in accordance with the Telephone Consumer Protection Act (TCPA)
            and all applicable FCC regulations.
          </ListItem>
          <ListItem>
            Our SMS practices comply with the CTIA Messaging Principles and Best Practices.
          </ListItem>
          <ListItem>
            For any user under the age of 13, PlayMaker Pics relies on verifiable parental consent
            as required by the Children's Online Privacy Protection Act (COPPA).
          </ListItem>
          <ListItem>
            All SMS messages include opt-out instructions.
          </ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>Supported Carriers</Heading>
        <Text mb={3}>
          Our SMS program is supported on all major U.S. wireless carriers including but not limited
          to AT&T, T-Mobile, Verizon, Sprint, and their affiliates. Carriers are not liable for
          delayed or undelivered messages.
        </Text>
      </Box>

      <Divider my={4} />

      <Box>
        <Heading as="h2" size="lg" mb={4}>Contact Information</Heading>
        <Text mb={2}><strong>Setora, LLC</strong> (operating as PlayMaker Pics)</Text>
        <Text mb={2}>
          Email:{' '}
          <Link href="mailto:support@playmakerpics.com" color="blue.500">support@playmakerpics.com</Link>
        </Text>
        <Text mb={4}>
          Website:{' '}
          <Link href="https://www.playmakerpics.com" color="blue.500" isExternal>www.playmakerpics.com</Link>
        </Text>
        <VStack spacing={1} align="start">
          <Link as={RouterLink} to="/privacy" color="blue.500">Privacy Policy</Link>
          <Link as={RouterLink} to="/terms" color="blue.500">Terms of Service</Link>
        </VStack>
      </Box>
    </VStack>
    <Footer />
  </Container>
);

export default OptInPolicy;
